import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TorriLogo } from "@/components/TorriLogo";
import ElectricVesta from "./HearthVesta";

export const COPPER = "#C17F59";

export const VestaFrame = ({ title, subtitle, children }) => (
  <div className="flex min-h-screen items-center justify-center bg-black p-6 text-white" data-testid="vesta-auth-page">
    <div className="w-full max-w-sm animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="mb-10 flex items-center justify-between">
        <TorriLogo imgClass="h-[46px]" />
        <span className="py-2 pl-2 text-[15px] font-extrabold tracking-[0.3em]" data-testid="vesta-auth-electric"><ElectricVesta /></span>
      </div>
      <h1 className="mb-3 font-heading text-3xl font-extrabold leading-tight tracking-tight">{title}</h1>
      {subtitle && <p className="mb-8 text-sm text-neutral-400">{subtitle}</p>}
      {children}
    </div>
  </div>
);

const Field = ({ id, label, ...p }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs uppercase tracking-widest text-neutral-400">{label}</Label>
    <Input id={id} className="border-neutral-800 bg-neutral-950 focus-visible:ring-[#C17F59]" required {...p} />
  </div>
);

const CopperButton = ({ loading, children, ...p }) => (
  <button type="submit" disabled={loading} {...p}
    className="flex w-full items-center justify-center gap-1 rounded-full py-3.5 text-sm font-bold text-black transition-[filter,transform] duration-200 hover:brightness-110 active:scale-[0.98] disabled:opacity-60"
    style={{ background: COPPER }}>
    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : children}
  </button>
);

const ErrorBox = ({ msg }) => msg ? <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400" data-testid="vesta-error">{msg}</div> : null;

export function VestaLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    const r = await login(email.trim(), password, "/vesta/login");
    setLoading(false);
    if (r.ok) { const next = new URLSearchParams(window.location.search).get("next"); nav(next && /^\/vesta\/[a-z/-]*$/.test(next) ? next : "/vesta/home"); } else setErr(r.error);
  };
  return (
    <VestaFrame title={<>Benvenuto in <span style={{ color: COPPER }}>Ai VESTA</span>, l'ecosistema integrato con intelligenza artificiale.</>}
      subtitle="Accedi ai servizi Torri riservati alla rete Partner, ovunque tu sia e da un'unica piattaforma.">
      <form onSubmit={submit} className="space-y-5">
        <Field id="v-email" label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@azienda.it" data-testid="vesta-login-email" />
        <Field id="v-pass" label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="vesta-login-password" />
        <ErrorBox msg={err} />
        <CopperButton loading={loading} data-testid="vesta-login-submit">Entra in Ai VESTA <ArrowRight className="ml-1 h-4 w-4" /></CopperButton>
      </form>
      <div className="mt-6 flex justify-between text-xs text-neutral-400">
        <Link to="/vesta/recupera" className="hover:text-white" data-testid="vesta-forgot-link">Password dimenticata?</Link>
        <Link to="/login" className="hover:text-white" data-testid="vesta-back-rete">Accesso rete Torri</Link>
      </div>
    </VestaFrame>
  );
}

export function VestaForgot() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const { data } = await api.post("/vesta/forgot-password", { email: email.trim() }); setMsg(data.message); }
    catch (e2) { setMsg(formatApiError(e2.response?.data?.detail) || "Riprova tra qualche minuto."); }
    setLoading(false);
  };
  return (
    <VestaFrame title="Password dimenticata" subtitle="Inserisci la tua email: ti invieremo un link per sceglierne una nuova.">
      {msg ? <p className="rounded-md border border-neutral-800 bg-neutral-950 p-3 text-sm text-neutral-300" data-testid="vesta-forgot-done">{msg}</p> : (
        <form onSubmit={submit} className="space-y-5">
          <Field id="f-email" label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="vesta-forgot-email" />
          <CopperButton loading={loading} data-testid="vesta-forgot-submit">Invia il link</CopperButton>
        </form>
      )}
      <Link to="/vesta" className="mt-6 inline-block text-xs text-neutral-400 hover:text-white" data-testid="vesta-back-login">← Torna all'accesso</Link>
    </VestaFrame>
  );
}

export function VestaPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const reset = sp.get("reset") === "1";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.length < 8) return setErr("La password deve avere almeno 8 caratteri");
    if (pw !== pw2) return setErr("Le due password non coincidono");
    setLoading(true);
    try { await api.post("/vesta/set-password", { token: sp.get("token") || "", password: pw }); nav("/vesta?ok=1"); }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
    setLoading(false);
  };
  return (
    <VestaFrame title={reset ? "Nuova password" : "Attiva il tuo accesso"} subtitle="Scegli una password di almeno 8 caratteri.">
      <form onSubmit={submit} className="space-y-5">
        <Field id="p1" label="Password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="vesta-pw1" />
        <Field id="p2" label="Ripeti password" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="vesta-pw2" />
        <ErrorBox msg={err} />
        <CopperButton loading={loading} data-testid="vesta-pw-submit">Salva e accedi</CopperButton>
      </form>
      <Link to="/vesta" className="mt-6 inline-block text-xs text-neutral-400 hover:text-white">← Torna all'accesso</Link>
    </VestaFrame>
  );
}
